import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Link2, Copy, ClipboardList, Loader2 } from 'lucide-react';

interface LinkRow { id: string; token?: string; client_name?: string; broker_name?: string; template_name?: string; uses?: number; views?: number }
interface SubRow { id: string; client_name?: string; broker_name?: string; template_name?: string; site: Record<string, string>; answer_count: number; submitted_at: string }

const intakeUrl = (token: string): string => `${window.location.origin}/intake/${token}`;

/** Read-only view of a broker's or client's active intake links + pending sites.
 *  Creating / regenerating / approving all stays in the Pending Sites page. */
export const SiteIntakeReadonly: React.FC<{ brokerId?: string; clientId?: string }> = ({ brokerId, clientId }) => {
  const [links, setLinks] = useState<LinkRow[]>([]);
  const [subs, setSubs] = useState<SubRow[]>([]);
  const [loading, setLoading] = useState(true);

  const qs = brokerId ? `broker_id=${brokerId}` : clientId ? `client_id=${clientId}` : '';

  const load = useCallback(async () => {
    if (!qs) return;
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        api.get<LinkRow[]>(`/site-intake/links?${qs}`),
        api.get<SubRow[]>(`/site-intake/submissions?status=pending&${qs}`),
      ]);
      setLinks(l.data || []);
      setSubs(s.data || []);
    } finally { setLoading(false); }
  }, [qs]);
  useEffect(() => { void load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="intake-readonly">
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Link2 size={16} className="text-raptor-blue" /> Active intake links · {links.length}</CardTitle>
          <CardDescription>View-only. Create, regenerate or stop links from <b>Pending Sites</b>.</CardDescription></CardHeader>
        <CardContent className="space-y-1.5">
          {loading && <div className="py-4 text-center text-raptor-navy/50"><Loader2 className="inline animate-spin" size={16} /></div>}
          {!loading && links.length === 0 && <p className="text-xs text-raptor-navy/50">No active links.</p>}
          {!loading && links.map((l) => (
            <div key={l.id} className="flex items-center gap-2 rounded-md border border-border/70 bg-raptor-neutral/20 px-2.5 py-1.5 text-sm" data-testid={`ro-link-${l.id}`}>
              <span className="text-raptor-navy/60 truncate max-w-[110px] shrink-0" title={l.broker_name || 'Direct'}>{l.broker_name || 'Direct'}</span>
              <span className="text-raptor-navy/30 shrink-0">›</span>
              <span className="font-medium text-raptor-navy truncate max-w-[130px]" title={l.client_name || ''}>{l.client_name || '—'}</span>
              <Badge variant="outline" className="shrink-0 text-[11px]">{l.template_name || 'Survey'}</Badge>
              <span className="flex items-center gap-2 text-[11px] text-raptor-navy/60 font-tabular shrink-0">
                <span>Clicks <b className="text-raptor-navy">{l.views || 0}</b></span>
                <span>Submitted <b className="text-raptor-navy">{l.uses || 0}</b></span>
              </span>
              <Button size="sm" variant="outline" className="ml-auto shrink-0" disabled={!l.token}
                onClick={() => { navigator.clipboard?.writeText(intakeUrl(l.token!)); toast.success('Copied'); }} data-testid={`ro-copy-${l.id}`}>
                <Copy size={13} /> Copy link
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><ClipboardList size={16} className="text-raptor-blue" /> Pending sites · {subs.length}</CardTitle>
          <CardDescription>View-only. Approve or reject from <b>Pending Sites</b>.</CardDescription></CardHeader>
        <CardContent className="p-0 overflow-auto">
          <table className="w-full text-sm font-body">
            <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
              <tr>
                <th className="py-2 px-3 text-left">Site</th>
                <th className="py-2 px-3 text-left">Address</th>
                <th className="py-2 px-3 text-left">Survey</th>
                <th className="py-2 px-3 text-left">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={4} className="py-6 text-center text-raptor-navy/50"><Loader2 className="inline animate-spin" size={16} /></td></tr>}
              {!loading && subs.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-raptor-navy/50">No pending sites.</td></tr>}
              {!loading && subs.map((s) => (
                <tr key={s.id} className="border-t border-border/60" data-testid={`ro-sub-${s.id}`}>
                  <td className="py-2 px-3 font-medium text-raptor-navy">{s.site?.name || '—'}</td>
                  <td className="py-2 px-3 text-xs text-raptor-navy/70">{s.site?.physical_address || '—'}</td>
                  <td className="py-2 px-3 text-xs">{s.template_name} · {s.answer_count} ans</td>
                  <td className="py-2 px-3 font-tabular text-xs">{s.submitted_at ? new Date(s.submitted_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default SiteIntakeReadonly;
